import {
    LocationsResource,
    ConsolidatedLocationView,
    LocationEntityLink,
    MultiAgencyClassificationDto,
    CadResolveLocationsRequest,
} from '@mark43/cad-api';
import {
    MasterlocationsclassifyResource,
    MasterlocationsresolveResource,
    MasterlocationsResource,
} from '@mark43/rms-api';
import { useApiClient } from '~/api-client';
import { useCallback, useState } from 'react';
import { useLocationBias } from '../../location-bias/use-location-bias';
import { isValidLatLng } from '../../location/helpers';
import { useUnitLocationStore } from '../../fr-unit-location/use-unit-location-store';
import { mapDispatchAreasForLocationSearch } from '../helpers/data-mapping';
import { LocationStoreView } from '../types/locations';
import { getAllForcedResults } from '../helpers/forced-results';
import { useEnabledFeatures } from '../../common/hooks/enabled-features/use-enabled-features';

interface LocationApi {
    searchLocations: (query: string) => Promise<LocationStoreView[]>;
    resolveEntityLinksForLocation: (
        location: ConsolidatedLocationView
    ) => Promise<ConsolidatedLocationView>;
    payForEsriLocation: (location: ConsolidatedLocationView) => Promise<void>;
    isLoadingResults: boolean;
    errorLoadingLocationData: boolean;
    isClassifyingLocation: boolean;
}

export type ClassificationStrategy = 'SINGLE_AGENCY' | 'MULTI_AGENCY';

export function useLocationApi(
    classificationStrategy: ClassificationStrategy = 'SINGLE_AGENCY'
): LocationApi {
    const { request } = useApiClient();
    const [isLoadingResults, setIsLoading] = useState(false);
    const [errorLoadingLocationData, setErrorLoadingLocationData] = useState(false);
    const [isClassifyingLocation, setIsClassifying] = useState(false);
    const {
        filterLocationByDispatchAreaEnabled,
        subpremiseSupport,
        rollOutCadLocationResolutionEndpointEnabled,
    } = useEnabledFeatures();
    const locationBiasContextValue = useLocationBias();

    const unitLocationStore = useUnitLocationStore();
    const locationBiasId = locationBiasContextValue.locationBias?.value;

    const classifyLocationSingleAgency = useCallback(
        async (location: ConsolidatedLocationView): Promise<LocationEntityLink | undefined> => {
            return (
                await request<MasterlocationsclassifyResource.CadClassifyLocation>({
                    method: 'POST',
                    url: 'master/locations/classify/cad',
                    // ConsolidatedLocationView is mostly sufficient, but the endpoint expects
                    // a Location with a fullAddress which is equivalent to ConsolidatedLocationView.streetAddress
                    // @ts-expect-error
                    data: { ...location, fullAddress: location.streetAddress },
                    service: 'RMS',
                })
            ).data.data;
        },
        [request]
    );

    const classifyLocationMultiAgency = useCallback(
        async (
            location: ConsolidatedLocationView
        ): Promise<MultiAgencyClassificationDto[] | undefined> => {
            return (
                await request<MasterlocationsclassifyResource.MultiAgencyClassifyLocation>({
                    method: 'POST',
                    url: 'master/locations/classify/multiagency/',
                    // @ts-expect-error ConsolidatedLocationView is sufficient
                    data: location,
                    service: 'RMS',
                })
            ).data.data;
        },
        [request]
    );

    const resolveEntityLinksForLocation = useCallback(
        async function resolveEntityLinksForLocation(
            location: ConsolidatedLocationView
        ): Promise<ConsolidatedLocationView> {
            setIsClassifying(true);
            if (classificationStrategy === 'SINGLE_AGENCY') {
                // if locations are already classified, we don't reclassify them
                if (location.entityLinks[0]) {
                    setIsClassifying(false);
                    return location;
                }
                let resolvedAddress = location;
                try {
                    const classificationInfo = await classifyLocationSingleAgency(location);
                    if (classificationInfo !== undefined) {
                        resolvedAddress = {
                            ...location,
                            entityLinks: [...location.entityLinks, classificationInfo],
                        };
                    }
                } catch (error) {
                    //
                } finally {
                    setIsClassifying(false);
                }
                return resolvedAddress;
            } else {
                try {
                    const classificationInfo = await classifyLocationMultiAgency(location);
                    location.multiAgencyClassificationDtos = classificationInfo ?? [];
                } catch (error) {
                    //
                } finally {
                    setIsClassifying(false);
                }
                return location;
            }
        },
        [classificationStrategy, classifyLocationSingleAgency, classifyLocationMultiAgency]
    );

    /**
     * Every time the user selects a location in the location search, if it is an Esri
     * location, we have to make a call to the BE to potentially pay for its storage.
     */
    const payForEsriLocation = useCallback(
        async (view: ConsolidatedLocationView): Promise<void> => {
            if (view?.source === 'ESRI' && view?.sourceId !== undefined) {
                request<MasterlocationsResource.FetchLocationBySource>({
                    service: 'RMS',
                    method: 'GET',
                    params: {
                        source: view.source,
                        id: view.sourceId,
                        returnEntityLink: false,
                    },
                    url: `master/locations/`,
                    shouldFailSilently: true,
                });
            }
        },
        [request]
    );

    const doSearch = useCallback(
        async function doSearch(query: string): Promise<ConsolidatedLocationView[]> {
            try {
                setErrorLoadingLocationData(false);
                // A blank/whitespace query yields no results from any backend resolver and, on the
                // CAD endpoint, is rejected with a 400 (`Strings43.trimToNull` guard in
                // LocationsResource, TRI-21353). Short-circuit it here so every caller is covered
                // regardless of its own input validation. NOTE: this is a blank check only — the
                // backend has no minimum-length floor; ungated resolvers (GEO_ALIAS, street, point,
                // lat/long) resolve 1-2 character queries, so we must not gate on length.
                if (!query?.trim()) {
                    return [];
                }
                if (!rollOutCadLocationResolutionEndpointEnabled) {
                    const results = await request<MasterlocationsresolveResource.ResolveAllLocations>(
                        {
                            method: 'GET',
                            url: 'master/locations/resolve/all/',
                            service: 'RMS',
                            params: {
                                q: query,
                            },
                        }
                    );
                    return results.data.data ?? [];
                }
                const data: CadResolveLocationsRequest = { query };
                if (filterLocationByDispatchAreaEnabled) {
                    const unitGpsInfo = unitLocationStore?.unitGpsInfo;
                    const geoPoint =
                        unitGpsInfo && isValidLatLng(unitGpsInfo.lat, unitGpsInfo.lng)
                            ? { latitude: unitGpsInfo.lat, longitude: unitGpsInfo.lng }
                            : undefined;
                    data.dispatchAreaId = locationBiasId;
                    data.geoPoint = geoPoint;
                }
                const results = await request<LocationsResource.Resolve>({
                    method: 'POST',
                    url: 'locations/resolve/',
                    data,
                });
                return results.data.data?.consolidatedLocationViews ?? [];
            } catch (e) {
                setErrorLoadingLocationData(true);
                return [];
            }
        },
        [
            request,
            locationBiasId,
            unitLocationStore,
            filterLocationByDispatchAreaEnabled,
            rollOutCadLocationResolutionEndpointEnabled,
        ]
    );

    const searchLocations = useCallback(
        async function searchLocations(query: string): Promise<LocationStoreView[]> {
            try {
                setIsLoading(true);
                const results = await doSearch(query);
                const mappedResults = mapDispatchAreasForLocationSearch(results);

                return mappedResults.concat(
                    getAllForcedResults(query, subpremiseSupport, mappedResults[0])
                );
            } catch (e) {
                return [];
            } finally {
                setIsLoading(false);
            }
        },
        [doSearch, subpremiseSupport]
    );

    return {
        searchLocations,
        isLoadingResults,
        errorLoadingLocationData,
        isClassifyingLocation,
        resolveEntityLinksForLocation,
        payForEsriLocation,
    };
}
